import { textoItens } from '../CampoItens';
import { textoNd } from '../CampoNdVinculada';
import { useState, useMemo } from 'react';
import { ChevronDownIcon, ChevronUpIcon, PlusIcon, XMarkIcon, LockOpenIcon, PencilIcon, CheckIcon } from '@heroicons/react/24/outline';
import type { Card, Etapa, CampoConfig } from '../../types';
import { generateShortCode } from '../../utils/shortCodeUtils';

interface CardPaiProps {
  card: Card;
  etapa: Etapa;
  todasEtapas?: Etapa[]; // Para buscar campos travados de outras etapas
  onClick?: () => void;
  onClickFilho?: (filho: Card) => void;
  onAdicionarFilho?: (cardPai: Card) => void; // Callback para adicionar filho manualmente
  onRemoverFilho?: (cardPai: Card, filhoId: string) => void; // Callback para remover filho
  onDestravarPai?: (cardId: string) => void; // Callback para destravar card pai
  onUpdateValorTotal?: (cardId: string, valorTotal: number) => void; // Callback para atualizar valor total
  isDragging?: boolean;
  mostrarContagemFilhos?: boolean;
}

const CardPai = ({
  card,
  etapa,
  todasEtapas = [],
  onClick,
  onClickFilho,
  onAdicionarFilho,
  onRemoverFilho,
  onDestravarPai,
  onUpdateValorTotal,
  isDragging = false,
  mostrarContagemFilhos = true
}: CardPaiProps) => {
  const [expandido, setExpandido] = useState(false);
  const [editandoValorTotal, setEditandoValorTotal] = useState(false);
  const [valorTotalInput, setValorTotalInput] = useState('');

  const filhos = card.cardsFilhos || [];
  const totalFilhos = filhos.length;
  
  // Obter campos visíveis + campos travados
  const camposVisiveis = (() => {
    // 1. Buscar TODOS os campos travados de TODAS as etapas
    const todosCamposTravados = new Map(); // id -> campo
    todasEtapas.forEach(e => {
      if (e.camposTravados && e.camposTravados.length > 0) {
        e.campos.forEach(c => {
          if (e.camposTravados.includes(c.id)) {
            todosCamposTravados.set(c.id, c);
          }
        });
      }
    });
    
    // 2. Campos visíveis configurados da etapa atual
    let camposVisiveisConfig = etapa.camposVisiveisNoCard && etapa.camposVisiveisNoCard.length > 0
      ? etapa.campos.filter(campo => etapa.camposVisiveisNoCard?.includes(campo.id))
      : [];
    
    // 3. Adicionar campos travados que não estão na lista de visíveis
    const camposTravadosArray = Array.from(todosCamposTravados.values());
    const travadosNaoVisiveis = camposTravadosArray.filter(
      c => !camposVisiveisConfig.find(v => v.id === c.id)
    );
    
    // 4. Combinar: visíveis + travados não visíveis
    let camposParaMostrar = [...camposVisiveisConfig, ...travadosNaoVisiveis];
    
    // 5. Se não tem nada configurado (sem visíveis e sem travados), mostrar primeiros campos
    if (camposParaMostrar.length === 0) {
      camposParaMostrar = etapa.campos?.slice(0, 4) || [];
    }
    
    return camposParaMostrar;
  })();

  // Helper: converter valor brasileiro (53,98) ou americano (53.98) para número
  const parseValorBrasileiro = (valor: any): number => {
    if (valor === undefined || valor === null || valor === '') return 0;
    if (typeof valor === 'number') return isNaN(valor) ? 0 : valor;
    const valorStr = String(valor).trim();
    // Se tem vírgula, é formato brasileiro: 1.234,56 -> remove pontos e troca vírgula por ponto
    if (valorStr.includes(',')) {
      const valorConvertido = valorStr.replace(/\./g, '').replace(',', '.');
      const numero = Number(valorConvertido);
      return isNaN(numero) ? 0 : numero;
    }
    // Se só tem ponto (ou nenhum), já está em formato válido: 1234.56
    const numero = Number(valorStr);
    return isNaN(numero) ? 0 : numero;
  };

  // Helper: buscar valor de um campo no card, verificando campos atuais e depois etapaHistory
  const getValorCampo = (targetCard: Card, campoId: string) => {
    // PRIMEIRO: card.campos (fonte da verdade - valores atuais/editados)
    if (targetCard?.campos?.[campoId] !== undefined && targetCard.campos[campoId] !== '') {
      return targetCard.campos[campoId];
    }
    // SEGUNDO: etapaHistory (fallback para campos que só existem em etapas anteriores)
    if (targetCard?.etapaHistory && Array.isArray(targetCard.etapaHistory)) {
      for (let i = targetCard.etapaHistory.length - 1; i >= 0; i--) {
        const histEntry = targetCard.etapaHistory[i];
        if (histEntry?.camposSnapshot?.[campoId] !== undefined && histEntry.camposSnapshot[campoId] !== '') {
          return histEntry.camposSnapshot[campoId];
        }
      }
    }
    return undefined;
  };

  // Encontrar o primeiro campo do tipo 'valor' em TODAS as etapas (pode estar em outra etapa)
  const campoValor = useMemo(() => {
    // Primeiro tentar na etapa atual
    const campoLocal = etapa.campos?.find(c => c.tipo === 'valor');
    if (campoLocal) return campoLocal;
    // Se não encontrou, buscar em todas as etapas
    for (const e of todasEtapas) {
      const campo = e.campos?.find(c => c.tipo === 'valor');
      if (campo) return campo;
    }
    return null;
  }, [etapa.campos, todasEtapas]);

  // Calcular o valor total automaticamente (soma do campo valor do pai + filhos)
  const valorTotalCalculado = useMemo(() => {
    if (!campoValor) return 0;
    // Incluir valor do próprio card pai
    let soma = parseValorBrasileiro(getValorCampo(card, campoValor.id));
    // Somar valores dos filhos
    filhos.forEach(filho => {
      soma += parseValorBrasileiro(getValorCampo(filho, campoValor.id));
    });
    return soma;
  }, [campoValor, card, filhos]);

  // Valor total exibido: salvo no card ou calculado automaticamente
  const valorTotalExibido = (card.valorTotal != null && !isNaN(card.valorTotal)) 
    ? card.valorTotal 
    : valorTotalCalculado;

  const formatarMoeda = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const handleSalvarValorTotal = () => {
    const valorNumerico = parseFloat(valorTotalInput.replace(/[^\d.,\-]/g, '').replace(',', '.'));
    if (!isNaN(valorNumerico) && onUpdateValorTotal) {
      onUpdateValorTotal(card.id, valorNumerico);
    }
    setEditandoValorTotal(false);
  };

  const renderCampoValor = (campo: CampoConfig, valor: any) => {
    if (valor === undefined || valor === null || valor === '') {
      return <div className="text-sm text-gray-400 italic">-</div>;
    }

    switch (campo.tipo) {
      case 'texto':
      case 'texto_longo':
        return <div className="text-sm text-gray-600">{String(valor)}</div>;
      
      case 'data':
      case 'data_vencimento':
        try {
          const data = new Date(valor);
          return (
            <div className="text-sm text-gray-600">
              {data.toLocaleDateString('pt-BR')}
            </div>
          );
        } catch {
          return <div className="text-sm text-gray-600">{String(valor)}</div>;
        }
      
      case 'lista':
        return <div className="text-sm text-gray-600">{String(valor)}</div>;
      
      case 'itens':
        return <div className="text-sm text-gray-700">{textoItens(valor) || '—'}</div>;

      case 'nd_vinculada':
        return <div className="text-sm text-gray-700">{textoNd(valor) || '—'}</div>;

      case 'parcelas':
        // parcelas são objetos {nome, paga, ...}: sem este caso a tela mostraria [object Object]
        if (Array.isArray(valor) && valor.length > 0) {
          const pagas = valor.filter((p: any) => p?.paga).length;
          return (
            <div className="text-sm text-gray-600">
              {pagas}/{valor.length} pagas · {valor.map((p: any) => `${p?.nome ?? ''}${p?.paga ? ' ✓' : ''}`).join(', ')}
            </div>
          );
        }
        return null;

      case 'checkbox':
      case 'checkbox_single':
      case 'checkbox_multi':
        if (Array.isArray(valor)) {
          return (
            <div className="text-sm text-gray-600">
              {valor.filter(Boolean).join(', ')}
            </div>
          );
        }
        return null;
      
      case 'valor':
        return (
          <div className="text-sm text-gray-600 font-semibold">
            {new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL'
            }).format(parseValorBrasileiro(valor))}
          </div>
        );

      case 'anexo': {
        const arquivos = Array.isArray(valor) ? valor : valor ? [valor] : [];
        if (arquivos.length === 0) return <div className="text-sm text-gray-400 italic">-</div>;
        return (
          <div className="text-sm text-gray-600">
            {arquivos.map((arquivo: any, idx: number) => {
              const nome = typeof arquivo === 'string' ? arquivo : (arquivo?.name || arquivo?.fileName || arquivo?.originalName || 'Arquivo');
              const url = typeof arquivo === 'object' ? (arquivo?.webViewLink || arquivo?.webContentLink || arquivo?.url || arquivo?.dataUrl) : null;
              return (
                <div key={idx} className="flex items-center gap-1 truncate">
                  <span className="text-[10px]">📎</span>
                  {url ? (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate" onClick={(e) => e.stopPropagation()}>
                      {nome}
                    </a>
                  ) : (
                    <span className="truncate">{nome}</span>
                  )}
                </div>
              );
            })}
          </div>
        );
      }

      default:
        return <div className="text-sm text-gray-600">{String(valor)}</div>;
    }
  };

  const mostrarCont = mostrarContagemFilhos !== false;
  const gradId = `shieldGrad-${card.id}`;

  return (
    <div
      className={`
        relative group
        transition-all duration-200
        ${isDragging ? 'opacity-50 scale-95' : 'opacity-100 scale-100'}
      `}
    >
      <div
        className={`
          relative
          bg-gradient-to-b from-purple-50 to-white
          border rounded-xl
          transition-all duration-200
          ${expandido
            ? 'border-purple-300 shadow-lg shadow-purple-500/10'
            : 'border-purple-100 shadow-sm hover:shadow-lg hover:shadow-purple-500/10 hover:-translate-y-px'}
        `}
      >
        {/* Faixa roxa à esquerda (sob proteção do pai) */}
        <div className="absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-full bg-gradient-to-b from-purple-400 to-purple-600" />

        {/* Corpo do Card Pai */}
        <div onClick={onClick} className="cursor-pointer p-2.5 pl-3.5">
          {/* Header: emblema-escudo + título + chips */}
          <div className="flex items-start gap-2.5">
            {/* Emblema escudo com a contagem dentro */}
            <div className="relative shrink-0 w-9 h-10 drop-shadow-[0_3px_6px_rgba(139,92,246,0.35)]">
              <svg viewBox="0 0 40 44" fill="none" className="w-full h-full">
                <path
                  d="M20 2 4 7v13c0 11 8 18.5 16 22 8-3.5 16-11 16-22V7L20 2Z"
                  fill={`url(#${gradId})`}
                  stroke="#fff"
                  strokeOpacity="0.25"
                />
                <defs>
                  <linearGradient id={gradId} x1="20" y1="2" x2="20" y2="42" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#a78bfa" />
                    <stop offset="1" stopColor="#7c3aed" />
                  </linearGradient>
                </defs>
              </svg>
              {mostrarCont && totalFilhos > 0 && (
                <span className="absolute inset-0 grid place-items-center pb-1 text-white font-extrabold text-sm tabular-nums">
                  {totalFilhos}
                </span>
              )}
            </div>

            <div className="flex-1 min-w-0 pr-6">
              <h3 className="font-bold text-gray-900 text-[11px] leading-tight line-clamp-2">{card.titulo}</h3>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                  <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5l-8-3Z" />
                  </svg>
                  Card Pai
                </span>
                <span className="bg-gray-100 text-gray-500 text-[8px] px-1.5 py-0.5 rounded-full font-mono tracking-wide">
                  #{generateShortCode(card.id)}
                </span>
              </div>
            </div>
          </div>

          {/* Campos visíveis do Card Pai - linhas compactas (oculta vazios) */}
          {camposVisiveis.some(campo => {
            const v = card.campos[campo.id];
            return !(v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0));
          }) && (
            <div className="mt-2 space-y-0.5">
              {camposVisiveis.map(campo => {
                const valor = card.campos[campo.id];
                if (valor === undefined || valor === null || valor === '' || (Array.isArray(valor) && valor.length === 0)) return null;
                const conteudo = renderCampoValor(campo, valor);
                const isTravado = todasEtapas.some(e => e.camposTravados?.includes(campo.id));

                return (
                  <div key={campo.id} className="flex items-baseline gap-1 text-[10px] leading-tight min-w-0">
                    <span className="text-purple-400 flex-shrink-0 inline-flex items-center gap-0.5">
                      {campo.nome}:{isTravado && <span className="text-[8px]" title="Campo travado">🔒</span>}
                    </span>
                    <span className="text-gray-700 font-medium truncate min-w-0 flex-1 [&_div]:!inline [&_div]:!text-[10px] [&_div]:!text-gray-700 [&_div]:!font-medium">{conteudo}</span>
                  </div>
                );
              }).filter(Boolean)}
            </div>
          )}

          {/* Valor Total */}
          {campoValor && (
            <div className="mt-2 flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-2.5 py-1">
              <div className="text-[9px] font-bold uppercase tracking-wide text-green-700 flex items-center gap-1">
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4}>
                  <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
                Valor Total
              </div>
              {editandoValorTotal ? (
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="text"
                    value={valorTotalInput}
                    onChange={(e) => setValorTotalInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSalvarValorTotal();
                      if (e.key === 'Escape') setEditandoValorTotal(false);
                    }}
                    className="w-24 text-[10px] px-1.5 py-0.5 border border-green-300 rounded focus:ring-1 focus:ring-green-500 focus:border-green-500 text-right"
                    autoFocus
                    placeholder="0,00"
                  />
                  <button
                    onClick={handleSalvarValorTotal}
                    className="p-0.5 text-green-600 hover:text-green-800"
                    title="Salvar"
                  >
                    <CheckIcon className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => setEditandoValorTotal(false)}
                    className="p-0.5 text-gray-400 hover:text-gray-600"
                    title="Cancelar"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <span className="text-[13px] font-extrabold text-green-800 tabular-nums">
                    {formatarMoeda(valorTotalExibido)}
                  </span>
                  {onUpdateValorTotal && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setValorTotalInput(String(valorTotalExibido).replace('.', ','));
                        setEditandoValorTotal(true);
                      }}
                      className="p-0.5 text-green-500 hover:text-green-700 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Editar valor total"
                    >
                      <PencilIcon className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Ações: abrir escudo / adicionar filho / destravar */}
          <div className="flex gap-1 mt-2">
            {totalFilhos > 0 ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandido(!expandido);
                }}
                className="flex-1 min-w-0 flex items-center justify-center gap-1 py-1 px-1.5 bg-white border border-purple-200 hover:bg-purple-50 hover:border-purple-300 rounded-lg transition-colors text-purple-700 font-bold text-[10px]"
              >
                <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  {expandido ? (
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 018 0M5 11h14v9H5z" />
                  ) : (
                    <><rect x="5" y="11" width="14" height="9" rx="2" /><path strokeLinecap="round" strokeLinejoin="round" d="M8 11V8a4 4 0 018 0v3" /></>
                  )}
                </svg>
                <span className="truncate whitespace-nowrap">
                  {expandido ? 'Fechar escudo' : `Abrir escudo · ${totalFilhos}`}
                </span>
                {expandido
                  ? <ChevronUpIcon className="w-3 h-3 flex-shrink-0" />
                  : <ChevronDownIcon className="w-3 h-3 flex-shrink-0" />}
              </button>
            ) : onAdicionarFilho ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAdicionarFilho(card);
                }}
                className="flex-1 flex items-center justify-center gap-1 py-1.5 px-2 bg-indigo-50 text-indigo-600 border border-dashed border-indigo-300 rounded-lg hover:bg-indigo-100 transition-colors text-[10px] font-bold"
                title="Adicionar filho"
              >
                <PlusIcon className="w-3 h-3" />
                Adicionar filho
              </button>
            ) : null}

            {totalFilhos > 0 && onAdicionarFilho && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAdicionarFilho(card);
                }}
                className="px-2 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
                title="Adicionar filho"
              >
                <PlusIcon className="w-3.5 h-3.5" />
              </button>
            )}

            {onDestravarPai && totalFilhos === 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDestravarPai(card.id);
                }}
                className="px-2 py-1.5 bg-gray-100 text-gray-500 rounded-lg hover:bg-gray-200 transition-colors flex items-center"
                title="Destravar este card (transformar de volta em card normal)"
              >
                <LockOpenIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Gaveta dos filhos (abre/fecha com animação) */}
        {totalFilhos > 0 && (
          <div
            className={`grid transition-[grid-template-rows] duration-300 ease-out ${expandido ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
          >
            <div className="overflow-hidden">
              <div className="border-t border-purple-100 px-3 pt-2.5 pb-3">
                {/* Trilho ligando pai → filhos */}
                <div className="relative pl-4 space-y-2 max-h-52 overflow-y-auto">
                  <div className="absolute left-1.5 top-0 bottom-3 w-0.5 rounded bg-gradient-to-b from-indigo-400 to-indigo-200" />

                  {filhos.map((filho) => {
                    const valorFilho = campoValor ? getValorCampo(filho, campoValor.id) : undefined;
                    return (
                      <div
                        key={filho.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          onClickFilho?.(filho);
                        }}
                        className="relative bg-white border border-gray-100 rounded-lg shadow-sm hover:shadow-md hover:border-indigo-300 hover:translate-x-px transition-all cursor-pointer p-2 filho-card"
                      >
                        {/* Conector curvo do trilho até o filho */}
                        <div className="absolute -left-2.5 top-4 w-2.5 h-2 border-l-2 border-b-2 border-indigo-300 rounded-bl-lg" />

                        {/* Botão remover filho */}
                        {onRemoverFilho && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm(`Desvincular "${filho.titulo}" deste card pai?`)) {
                                onRemoverFilho(card, filho.id);
                              }
                            }}
                            className="absolute top-1.5 right-1.5 p-0.5 text-gray-300 hover:text-white hover:bg-red-500 rounded transition-all opacity-0 [.filho-card:hover_&]:opacity-100 z-10"
                            title="Desvincular filho"
                          >
                            <XMarkIcon className="w-3 h-3" />
                          </button>
                        )}

                        {/* Título + valor do filho */}
                        <div className="flex items-start justify-between gap-2 pr-4">
                          <h4 className="font-bold text-gray-900 text-[10px] leading-tight line-clamp-2">{filho.titulo}</h4>
                          {valorFilho !== undefined && valorFilho !== null && valorFilho !== '' && (
                            <span className="text-[10px] font-extrabold text-green-700 tabular-nums shrink-0">
                              {formatarMoeda(parseValorBrasileiro(valorFilho))}
                            </span>
                          )}
                        </div>

                        {/* Campos do filho como tags */}
                        {camposVisiveis.filter(c => c.id !== campoValor?.id).slice(0, 3).some(campo => getValorCampo(filho, campo.id)) && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {camposVisiveis.filter(c => c.id !== campoValor?.id).slice(0, 3).map(campo => {
                              const valor = getValorCampo(filho, campo.id);
                              if (valor === undefined || valor === null || valor === '' || (Array.isArray(valor) && valor.length === 0)) return null;
                              const texto = Array.isArray(valor)
                                ? valor.filter(Boolean).map((v: any) => (v && typeof v === 'object' && 'paga' in v)
                                    ? `${v.nome ?? ''}${v.paga ? ' ✓' : ''}` : String(v)).join(', ')
                                : String(valor);
                              if (!texto.trim()) return null;
                              return (
                                <span
                                  key={campo.id}
                                  className="max-w-[140px] truncate text-[9px] text-gray-600 bg-gray-50 border border-gray-100 px-1.5 py-0.5 rounded"
                                  title={`${campo.nome}: ${texto}`}
                                >
                                  {texto}
                                </span>
                              );
                            }).filter(Boolean)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Adicionar filho no fim da gaveta */}
                {onAdicionarFilho && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAdicionarFilho(card);
                    }}
                    className="mt-2 ml-4 inline-flex items-center gap-1.5 text-[10px] font-bold text-indigo-600 bg-indigo-50 border border-dashed border-indigo-300 px-3 py-1.5 rounded-lg hover:bg-indigo-100 transition-colors"
                  >
                    <PlusIcon className="w-3 h-3" />
                    Adicionar filho
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CardPai;
